import {get, set} from 'object-path-immutable'
import {State} from '.'
import {variableRegex} from '../../../common/themes'
import {
  BannerElement,
  CardDefinition,
  ContextMapping,
  UIElement
} from '../../../common/types'
import {
  ActionFlowConfig,
  ActionFlowStep,
  ExternalStep,
  RequestListenerStep
} from '../../../common/types/action-flows'
import {newSubview} from '../../../lib/card-element-helpers'
import {arrayDiff} from '../../../lib/helpers'
import {emptyCardDefinition} from '../../../selectors/card-template'
import {ActionFlowState} from './slice/flow/state'
import {deepmergeCustom} from 'deepmerge-ts'

// Return the URL path to this element key
export const elementKeyToUrlPath = (
  cardDefinition: CardDefinition,
  elementKey: string
): [string, UIElement | BannerElement] | undefined => {
  let found: UIElement | BannerElement | undefined =
    cardDefinition.defaultView.content.find(el => el.key === elementKey)
  if (found) {
    return [`/element/${elementKey}`, found]
  }
  found = cardDefinition.defaultView.actions.find(el => el.key === elementKey)
  if (found) {
    return [`/action/${elementKey}`, found]
  }
  if (cardDefinition.metadata?.banner?.key === elementKey) {
    return [`/banner/${elementKey}`, cardDefinition.metadata.banner]
  }
  for (const subview of Object.keys(cardDefinition.subviews)) {
    found = cardDefinition.subviews[subview].inputs.find(
      el => el.key === elementKey
    )
    if (found) {
      return [`/element/${elementKey}/s/${subview}`, found]
    }
    found = cardDefinition.subviews[subview].actions.find(
      el => el.key === elementKey
    )
    if (found) {
      return [`/action/${elementKey}/s/${subview}`, found]
    }
  }
  return undefined
}

// Return the path to UIElements inside a CardDefinition
// Not sure if we'll keep this as we likely want typed access to the state
// But this works with with object-path-immutable
export const getElementPath = ({
  subviewId,
  action
}: {
  subviewId?: string
  action?: boolean
}) => {
  return subviewId
    ? `subviews.${subviewId}.${action ? 'actions' : 'inputs'}`
    : action
    ? `defaultView.actions`
    : `defaultView.content`
}

// used when duplicating card elements
export const deepClone = <T extends UIElement>(obj: T): T =>
  structuredClone(obj)

export const updateFlow = (
  state: State,
  update: (flow: State['flow']) => State['flow']
): State => {
  return {
    ...state,
    flow: update(state.flow)
  }
}

export const updateConfig = (
  state: ActionFlowState,
  update: (prevConfig: ActionFlowConfig) => ActionFlowConfig
): ActionFlowState => {
  return {
    ...state,
    config: update(state.config)
  }
}

export const updateStep = (
  state: ActionFlowState,
  stepId: string,
  update: (prevStep: ActionFlowStep) => ActionFlowStep
): ActionFlowState => {
  return updateConfig(state, config => {
    const step = config.steps[stepId]
    // update the step
    let updatedStep = update(step)

    // and then update the associated context mapping
    // (removing any mappings that are no longer used)
    updatedStep =
      step.type === 'start' || updatedStep.type === 'start'
        ? // appease TS, start steps don't have context mapping
          updatedStep
        : updateContextMapping(step, updatedStep)

    return {
      ...config,
      steps: {...config.steps, ...{[stepId]: updatedStep}}
    }
  })
}

export const updateTrigger = (
  state: ActionFlowState,
  triggerId: string,
  update: (prevStep: ExternalStep) => ExternalStep
): ActionFlowState => {
  return updateConfig(state, config => {
    if (!config.triggers || !(triggerId in config.triggers)) {
      console.warn('Trigger not found', triggerId)
      return config
    }

    return {
      ...config,
      triggers: {
        ...(config.triggers ? config.triggers : {}),
        ...{[triggerId]: update(config.triggers[triggerId])}
      }
    }
  })
}

export const updateRequestListener = (
  state: ActionFlowState,
  triggerId: string,
  update: (prevStep: RequestListenerStep) => RequestListenerStep
): ActionFlowState => {
  return updateTrigger(state, triggerId, trigger => {
    if (trigger.type !== 'connector') {
      console.warn('Invalid trigger type', triggerId)
      return trigger
    }

    return update(trigger)
  })
}

export const updateCardTemplate = (
  state: ActionFlowState,
  templateId: string,
  update: (definition: CardDefinition) => CardDefinition
): ActionFlowState => {
  return updateConfig(state, config => {
    // Get the definition before we update it
    const definition: CardDefinition =
      (config?.cardTemplates?.[templateId] as CardDefinition) ??
      emptyCardDefinition

    // Update the definition
    const updatedDefinition = updateContextMapping(
      definition,
      update(definition)
    )

    return {
      ...config,
      cardTemplates: {
        ...config.cardTemplates,
        [templateId]: updatedDefinition
      }
    }
  })
}

const updateCardElement = <E extends UIElement>(
  state: ActionFlowState,
  {
    templateId,
    key,
    action,
    subviewId
  }: {
    templateId: string
    key: string
    action: boolean
    subviewId?: string
  },
  update: (element: E) => E
): ActionFlowState => {
  const path = getElementPath({subviewId, action})
  return updateCardTemplate(state, templateId, (definition: CardDefinition) => {
    const elements = get<CardDefinition, E[]>(definition, path, [])
    const idx = elements.findIndex(el => el.key === key)
    if (idx > -1) {
      const updated = update(elements[idx])
      return set(definition, `${path}.${idx}`, updated)
    }
    console.warn("can't find element at path", path)
    return definition
  })
}

export const updateCardElementAttributes = <E extends UIElement>(
  state: ActionFlowState,
  options: {
    templateId: string
    key: string
    action: boolean
    subviewId?: string
  },
  update: (attributes: E['attributes']) => E['attributes']
) => {
  return updateCardElement<E>(state, options, element => {
    return {...element, attributes: update(element.attributes)}
  })
}

export const updateCardElementValidations = <E extends UIElement>(
  state: ActionFlowState,
  options: {
    templateId: string
    key: string
    action: boolean
    subviewId?: string
  },
  update: (validators: E['validations']) => E['validations']
) => {
  return updateCardElement<E>(state, options, element => {
    return {...element, validations: update(element.validations)}
  })
}

export type Subview = CardDefinition['subviews'][0]

export const updateCardSubview = (
  state: ActionFlowState,
  templateId: string,
  subviewId: string,
  update: (subview: Subview) => Subview
) => {
  return updateCardTemplate(state, templateId, definition => {
    return {
      ...definition,
      subviews: {
        ...definition.subviews,
        [subviewId]: update(
          definition.subviews && subviewId in definition.subviews
            ? definition.subviews[subviewId]
            : newSubview('New Subview')
        )
      }
    }
  })
}

export const updateContextMapping = <
  C extends {contextMapping?: ContextMapping}
>(
  oldDefinition: C,
  newDefinition: C
): C => {
  // Get the context mappingIds before we updated the definition,
  const oldMappingIds = findPlaceholdersInDefinition(oldDefinition)
  // Get the context mappingIds after we updated the definition,
  const newMappingIds = findPlaceholdersInDefinition(newDefinition)
  // Get the mappingIds that were removed
  const removePlaceholderIds = arrayDiff(oldMappingIds, newMappingIds)
  // Prune the removed mappings from the definition
  return removePlaceholderMappings(newDefinition, removePlaceholderIds)
}

const findPlaceholdersInDefinition = <
  C extends {contextMapping?: ContextMapping}
>(
  definition: C
): string[] => {
  return [...JSON.stringify(definition).matchAll(variableRegex)].map(
    ([, mappingId]) => mappingId
  )
}

const removePlaceholderMappings = <C extends {contextMapping?: ContextMapping}>(
  definition: C,
  removePlaceholderIds: string[]
): C => {
  return {
    ...definition,
    contextMapping:
      removePlaceholderIds.length && definition.contextMapping
        ? Object.keys(definition.contextMapping).reduce((acc, mappingId) => {
            if (removePlaceholderIds.includes(mappingId)) {
              return acc
            }
            return {
              ...acc,
              [mappingId]: definition.contextMapping?.[mappingId]
            }
          }, {})
        : definition.contextMapping
  }
}

export const merge = deepmergeCustom({mergeArrays: false})
